import {DemoAnalysis} from "../api/models";

// Works out what an audio demo sounds like, for the player to draw (DemoPlayer): its waveform (the loudest sample in
// each of PEAKS slices) and its spectrum over time (BANDS frequency bands, FRAME_RATE times a second). The
// administration page does it in the browser, from the file being uploaded, so the demos' CDN needs no CORS; the API
// keeps the result. Levels are bytes (0-255), sent as base64.

export const PEAKS = 256;
// The API keeps demos up to an hour, and spectra up to 512 KB: an hour at these is 518,400 bytes.
export const BANDS = 12;
export const FRAME_RATE = 12;
const FFT_SIZE = 2048;
// The bands span this range, spaced evenly in pitch (log frequency).
const LOWEST_HZ = 40;
const HIGHEST_HZ = 16000;
// The spectrum's levels cover this many decibels below its loudest.
const RANGE_DB = 60;

/** Decodes the file (any format the browser plays) and analyses it. */
export async function analyseFile(file: Blob): Promise<DemoAnalysis> {
    const context = new OfflineAudioContext(1, 1, 44100);
    const audio = await context.decodeAudioData(await file.arrayBuffer());
    const channels = Array.from({ length: audio.numberOfChannels }, (_, i) => audio.getChannelData(i));
    return analyseSamples(channels, audio.sampleRate);
}

/** The analysis of decoded audio: its channels (mixed to mono) at sampleRate. */
export function analyseSamples(channels: Float32Array[], sampleRate: number): DemoAnalysis {
    const length = channels[0]?.length ?? 0;
    const mono = new Float32Array(length);
    for (const channel of channels) {
        for (let i = 0; i < length; i++) {
            mono[i] += channel[i] / channels.length;
        }
    }
    const duration = length / sampleRate;

    // Waveform: the loudest sample of each slice, against the loudest overall.
    const peaks = new Float64Array(PEAKS);
    for (let p = 0; p < PEAKS; p++) {
        const from = Math.floor(p * length / PEAKS), to = Math.max(from + 1, Math.floor((p + 1) * length / PEAKS));
        let max = 0;
        for (let i = from; i < to && i < length; i++) {
            max = Math.max(max, Math.abs(mono[i]));
        }
        peaks[p] = max;
    }
    const loudest = Math.max(...peaks, 1e-9);

    // Spectrum: a Hann-windowed FFT at the middle of each frame, each band's mean power in decibels.
    const frameRate = FRAME_RATE;
    const frames = Math.max(1, Math.round(duration * frameRate));
    const edges = bandEdges(sampleRate);
    const window = Float64Array.from({ length: FFT_SIZE }, (_, i) => 0.5 - 0.5 * Math.cos(2 * Math.PI * i / (FFT_SIZE - 1)));
    const levels = new Float64Array(frames * BANDS);
    const re = new Float64Array(FFT_SIZE), im = new Float64Array(FFT_SIZE);
    for (let f = 0; f < frames; f++) {
        const center = Math.round((f + 0.5) / frameRate * sampleRate);
        const start = center - FFT_SIZE / 2;
        for (let i = 0; i < FFT_SIZE; i++) {
            const sample = start + i >= 0 && start + i < length ? mono[start + i] : 0;
            re[i] = sample * window[i];
            im[i] = 0;
        }
        fft(re, im);
        for (let b = 0; b < BANDS; b++) {
            let power = 0;
            for (let k = edges[b]; k < edges[b + 1]; k++) {
                power += re[k] * re[k] + im[k] * im[k];
            }
            levels[f * BANDS + b] = 10 * Math.log10(power / (edges[b + 1] - edges[b]) + 1e-12);
        }
    }
    // A loop, not Math.max(...levels): a long demo has too many for one call's arguments.
    let top = -Infinity;
    for (const db of levels) {
        top = Math.max(top, db);
    }

    return {
        duration,
        peaks: toBase64(Array.from(peaks, x => Math.round(255 * x / loudest))),
        frameRate,
        bands: BANDS,
        spectrum: toBase64(Array.from(levels, db => Math.round(255 * Math.min(1, Math.max(0, (db - (top - RANGE_DB)) / RANGE_DB)))))
    };
}

// The FFT bins each band starts at, and (last) where the top one ends: at least one bin each.
function bandEdges(sampleRate: number): number[] {
    const binHz = sampleRate / FFT_SIZE;
    const highest = Math.min(HIGHEST_HZ, sampleRate / 2);
    const edges: number[] = [];
    for (let b = 0; b <= BANDS; b++) {
        const hz = LOWEST_HZ * (highest / LOWEST_HZ) ** (b / BANDS);
        edges.push(Math.max(b === 0 ? 1 : edges[b - 1] + 1, Math.round(hz / binHz)));
    }
    return edges;
}

// In-place radix-2 FFT (re.length a power of two).
function fft(re: Float64Array, im: Float64Array): void {
    const n = re.length;
    for (let i = 1, j = 0; i < n; i++) {
        let bit = n >> 1;
        for (; j & bit; bit >>= 1) {
            j ^= bit;
        }
        j ^= bit;
        if (i < j) {
            [re[i], re[j]] = [re[j], re[i]];
            [im[i], im[j]] = [im[j], im[i]];
        }
    }
    for (let size = 2; size <= n; size <<= 1) {
        const angle = -2 * Math.PI / size;
        for (let start = 0; start < n; start += size) {
            for (let k = 0; k < size / 2; k++) {
                const cos = Math.cos(angle * k), sin = Math.sin(angle * k);
                const a = start + k, b = a + size / 2;
                const tre = re[b] * cos - im[b] * sin, tim = re[b] * sin + im[b] * cos;
                re[b] = re[a] - tre;
                im[b] = im[a] - tim;
                re[a] += tre;
                im[a] += tim;
            }
        }
    }
}

function toBase64(bytes: number[]): string {
    let text = '';
    for (let i = 0; i < bytes.length; i += 0x8000) {
        text += String.fromCharCode(...bytes.slice(i, i + 0x8000));
    }
    return btoa(text);
}

/** An analysis's levels back as bytes (peaks or spectrum). */
export function fromBase64(value: string): Uint8Array {
    return Uint8Array.from(atob(value), c => c.charCodeAt(0));
}
